import type {
  PresentedChoice,
  Recommendation,
} from "@bindery/shared/reader/daily-reading";
import { Button } from "@bindery/ui/components/ui/button";
import { Skeleton } from "@bindery/ui/components/ui/skeleton";
import { cn } from "@bindery/ui/lib/utils";
import { RefreshCw } from "lucide-react";
import { Link } from "react-router";
import { starterWorksheetBook } from "./default-library";
import type { DailyRecommendations } from "./use-daily-recommendations";

export const UNTITLED_READING = "A short reading";

export function readingHref(readingId: string): string {
  return `/reader/${readingId}`;
}

const SKELETON_CARD_COUNT = 4;

const coverTints = [
  "bg-skyfall-500/40 text-skyfall-700",
  "bg-plumwood-100 text-plumwood-700",
  "bg-mintleaf-100 text-mintleaf-700",
  "bg-flamenco-400/30 text-flamenco-700",
] as const;

function coverTint(position: number): string {
  return coverTints[position % coverTints.length] ?? coverTints[0];
}

function ChoiceGrid({ children, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4" {...props}>
      {children}
    </ul>
  );
}

function ChoiceCard({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex">
      <Link
        to={to}
        className="min-h-touch-min focus-visible:ring-skyfall-600 flex w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-sm transition-transform outline-none focus-visible:ring-4 active:scale-[0.98] motion-reduce:transition-none"
      >
        {children}
      </Link>
    </li>
  );
}

function CoverImage({ src }: { src: string }) {
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      className="aspect-[3/4] w-full object-cover"
    />
  );
}

function TitleCover({ title, position }: { title: string; position: number }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "flex aspect-[3/4] w-full items-center justify-center p-4 text-center text-xl leading-tight font-semibold text-balance sm:text-2xl",
        coverTint(position),
      )}
    >
      <span className="line-clamp-4">{title}</span>
    </div>
  );
}

function ChoiceCaption({
  title,
  byline,
}: {
  title: string;
  byline: string | null;
}) {
  return (
    <div className="flex flex-1 flex-col gap-1 px-4 py-3">
      <span className="line-clamp-2 text-lg leading-snug font-semibold text-gray-900">
        {title}
      </span>
      {byline !== null && (
        <span className="line-clamp-1 text-sm text-gray-600">{byline}</span>
      )}
    </div>
  );
}

function recommendationByline(recommendation: Recommendation): string | null {
  return (
    recommendation.publication?.author ??
    recommendation.publication?.title ??
    null
  );
}

function RecommendationChoice({
  recommendation,
  position,
}: {
  recommendation: Recommendation;
  position: number;
}) {
  const title = recommendation.title ?? UNTITLED_READING;
  const cover = recommendation.publication?.coverImage ?? null;
  return (
    <ChoiceCard to={readingHref(recommendation.id)}>
      {cover === null ? (
        <TitleCover title={title} position={position} />
      ) : (
        <CoverImage src={cover} />
      )}
      <ChoiceCaption
        title={title}
        byline={recommendationByline(recommendation)}
      />
    </ChoiceCard>
  );
}

function StarterWorksheetChoice() {
  return (
    <ChoiceCard to={readingHref(starterWorksheetBook.reading_id)}>
      <CoverImage src={starterWorksheetBook.imageURL} />
      <ChoiceCaption
        title={starterWorksheetBook.title}
        byline="First sentences"
      />
    </ChoiceCard>
  );
}

function ChoiceSkeleton() {
  return (
    <li className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <Skeleton className="aspect-[3/4] w-full rounded-none" />
      <div className="flex flex-col gap-2 px-4 py-3">
        <Skeleton className="h-5 w-4/5" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </li>
  );
}

function SectionHeading({ title, lead }: { title: string; lead: string }) {
  return (
    <>
      <h2 className="text-2xl font-semibold tracking-tight text-gray-900">
        {title}
      </h2>
      <p className="mt-1 text-gray-600">{lead}</p>
    </>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <section className="bg-stone-50 px-6 py-10">
      <div className="mx-auto max-w-3xl">{children}</div>
    </section>
  );
}

function LoadingChoices() {
  return (
    <Section>
      <SectionHeading
        title="Pick a short reading"
        lead="Finish one today, or find a book below."
      />
      <ChoiceGrid aria-busy="true" aria-label="Finding short readings">
        {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
          <ChoiceSkeleton key={i} />
        ))}
      </ChoiceGrid>
    </Section>
  );
}

const BUSY_LABEL = "Finding readings…";

interface ChoiceControls {
  offline: boolean;
  refreshing: boolean;
  refresh: () => void;
}

function RefreshButton({
  label,
  refreshing,
  refresh,
}: {
  label: string;
  refreshing: boolean;
  refresh: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="lg"
      className="min-h-touch-min"
      disabled={refreshing}
      onClick={refresh}
    >
      <RefreshCw
        aria-hidden="true"
        className={cn(refreshing && "animate-spin motion-reduce:animate-none")}
      />
      {refreshing ? BUSY_LABEL : label}
    </Button>
  );
}

function ChoiceControlsRow({
  note,
  buttonLabel,
  offline,
  refreshing,
  refresh,
}: ChoiceControls & { note: string; buttonLabel: string }) {
  return (
    <div className="min-h-touch-min mt-6 flex flex-wrap items-center justify-between gap-4">
      <output className="text-sm text-gray-600">
        {refreshing ? (
          <span className="sr-only">{BUSY_LABEL}</span>
        ) : offline ? (
          note
        ) : null}
      </output>
      <RefreshButton
        label={buttonLabel}
        refreshing={refreshing}
        refresh={refresh}
      />
    </div>
  );
}

function StarterOnly(controls: ChoiceControls) {
  return (
    <Section>
      <SectionHeading
        title="Start here"
        lead="A page of first sentences to read, or find a book below."
      />
      <ChoiceGrid aria-label="Short readings" aria-busy={controls.refreshing}>
        <StarterWorksheetChoice />
      </ChoiceGrid>
      {(controls.offline || controls.refreshing) && (
        <ChoiceControlsRow
          note="More readings arrive when this device is back online."
          buttonLabel="Look for readings again"
          {...controls}
        />
      )}
    </Section>
  );
}

function isStarterOnly(choices: readonly PresentedChoice[]): boolean {
  return choices.every((choice) => choice.kind === "starter_worksheet");
}

function ReadyChoices({
  choices,
  ...controls
}: ChoiceControls & { choices: readonly PresentedChoice[] }) {
  return (
    <Section>
      <SectionHeading
        title="Pick a short reading"
        lead="Finish one today, or find a book below."
      />
      <ChoiceGrid aria-label="Short readings" aria-busy={controls.refreshing}>
        {choices.map((choice, position) =>
          choice.kind === "recommendation" ? (
            <RecommendationChoice
              key={choice.recommendation.id}
              recommendation={choice.recommendation}
              position={position}
            />
          ) : (
            <StarterWorksheetChoice key="starter_worksheet" />
          ),
        )}
      </ChoiceGrid>
      <ChoiceControlsRow
        note="Saved on this device for reading offline."
        buttonLabel={
          controls.offline
            ? "Look for readings again"
            : "Show me other readings"
        }
        {...controls}
      />
    </Section>
  );
}

export function DailyRecommendationsSection(props: DailyRecommendations) {
  if (props.status === "loading") return <LoadingChoices />;
  const controls: ChoiceControls = {
    offline: props.offline,
    refreshing: props.refreshing,
    refresh: props.refresh,
  };
  if (isStarterOnly(props.choices)) return <StarterOnly {...controls} />;
  return <ReadyChoices choices={props.choices} {...controls} />;
}
